import {
  Dialog,
  DialogMaxWidth,
  DialogVariant
} from '@focaldata/cin-ui-components'
import React, { useCallback, useRef, useState } from 'react'
import { EntryType, QuestionKind } from '../../../data/model/questionnaire'
import {
  RawQuestionnaireEntry,
  RawQuestionnaireEntryInput
} from '../../../hooks/import/models'
import useGetDraftQuestionnaire from '../../../hooks/questionnaire/useGetDraftQuestionnaire'
import { useDialogHooks } from './Dialog.hooks'
import FilePicker from './DocxFileInput/FilePicker'
import useDocxProcessor from './DocxProcessor/DocxProcessor.hooks'
import ImportListPreview from './ImportListPreview'
import { useImportValidation } from './ImportValidation.hooks'
import { ImportValidationHint } from './ImportValidationHint'
import { Pair, transformPairToImportEntry } from './import.utils'

const ImportFromFileDialog: React.FC = () => {
  const getRawQuestionnaireFromDocx = useDocxProcessor()
  const { validationError, validateMapping, clearValidation } =
    useImportValidation()
  const dataToImport = useRef<RawQuestionnaireEntryInput[]>([])
  const [isImportButtonDisabled, setIsImportButtonDisabled] = useState(true)

  const [importedQuestions, setImportedQuestions] =
    React.useState<RawQuestionnaireEntry[]>()

  const handleResetPreview = () => {
    setImportedQuestions([])
    dataToImport.current = []
    setIsImportButtonDisabled(true)
    clearValidation()
  }

  const { flattenedEntries } = useGetDraftQuestionnaire()
  const filteredQuestionnaireEntries = flattenedEntries.filter(
    (q) =>
      ![EntryType.ForkEntryType, EntryType.SectionEntryType].includes(
        q.entryType
      ) && q.questionKind === QuestionKind.QuestionnaireKind
  )

  const { isDialogOpen, isLoading, handleDialogClose, handleDialogSave } =
    useDialogHooks(handleResetPreview)

  const handleFileSelected = useCallback(
    async (file: File) => {
      const rawQuestions = await getRawQuestionnaireFromDocx(file)
      const importedQuestionnaireQuestions = rawQuestions.filter(
        (q) => q.kind === QuestionKind.QuestionnaireKind
      )
      if (importedQuestionnaireQuestions.length > 0) {
        setImportedQuestions(importedQuestionnaireQuestions)
      }
    },
    [getRawQuestionnaireFromDocx]
  )

  const handleMappingChanged = (newPairs: Pair[]) => {
    const entriesToImport = newPairs.flatMap((pair) => {
      const entry = transformPairToImportEntry(pair)
      return entry ? [entry] : []
    })

    const isValid = validateMapping(newPairs)
    setIsImportButtonDisabled(entriesToImport.length === 0 || !isValid)
    dataToImport.current = entriesToImport
  }

  return (
    <Dialog
      title="Import Survey (.docx)"
      primaryButtonText="Import"
      open={isDialogOpen}
      fullWidth
      primaryButtonLoading={isLoading}
      primaryButtonDisabled={isImportButtonDisabled}
      statusComponent={
        validationError && <ImportValidationHint message={validationError} />
      }
      variant={
        validationError
          ? DialogVariant.SingleButtonStatus
          : DialogVariant.SingleButton
      }
      maxWidth={
        filteredQuestionnaireEntries.length
          ? DialogMaxWidth.Lg
          : DialogMaxWidth.Md
      }
      primaryButtonClick={() => handleDialogSave(dataToImport.current)}
      onClose={handleDialogClose}
    >
      <FilePicker
        onFileSelected={handleFileSelected}
        onFileRemoved={handleResetPreview}
      />
      {!!importedQuestions?.length && (
        <ImportListPreview
          questionnaireQuestions={filteredQuestionnaireEntries}
          importedQuestions={importedQuestions}
          onMappingChanged={handleMappingChanged}
        />
      )}
    </Dialog>
  )
}

export default ImportFromFileDialog
